// src/pages/memoirs/CreateMemoir.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, ArrowLeft, CautionIcon } from '../../../public/icons';
import {
  Achievment,
  Healing,
  LoveRelation,
  CheckImage,
  Family,
  Parenting,
  Business,
  LifeLessons,
  YourOwnStory,
  photoAlbumImage
} from '../../../public/images/memoir';
import { LegacyColorBook, LegacyColorBook2 } from '../../../public/images/legacy';
import { memoirService } from '../../../services/memoirService';

// Map book type IDs to their respective images (since images are local)
const bookTypeImages = {
  'achievement': Achievment,
  'healing': Healing,
  'love': LoveRelation,
  'family': Family,
  'parenting': Parenting,
  'business': Business,
  'life': LifeLessons,
  'own': YourOwnStory
};

const CreateMemoir = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [formData, setFormData] = useState({
    memoirFor: 'myself',
    pages: 100,
    copies: 2,
    theme: 'wine',
    questionFrequency: 'weekly',
    memoirTitle: '',
    memoirSubtitle: '',
    bookType: '',
    categoryId: '',
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    country: 'nigeria'
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await memoirService.getCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Map bookType to categoryId based on category name matching
  useEffect(() => {
    if (formData.bookType && categories.length > 0) {
      const categoryMap = {
        'achievement': 'Achievement & Becoming',
        'healing': 'Healing & Survival',
        'love': 'Love & Relationships',
        'family': 'Family & Roots',
        'parenting': 'Parenting (Motherhood & Fatherhood)',
        'business': 'Business Trajectory',
        'life': 'Life Lessons & Reflections',
        'own': 'Tell your own story'
      };

      const categoryName = categoryMap[formData.bookType];
      const category = categories.find((c) => c.name === categoryName);

      if (category) {
        setFormData((prev) => ({ ...prev, categoryId: category._id }));
      }
    }
  }, [formData.bookType, categories]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 6));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  const calculateTotal = () => {
    const basePrice = 250000;
    const additionalCopy =
      formData.copies > 1 ? (formData.copies - 1) * 250000 : 0;
    return basePrice + additionalCopy;
  };

  /* ═══════════════════════════════════════════════════════
     ORDER PLACEMENT — creates order + redirects to Paystack
     ═══════════════════════════════════════════════════════ */
  const handlePlaceOrder = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('kapta_token');
      const isGuestOrder = !token;

      const orderPayload = {
        memoirFor: formData.memoirFor,
        pages: formData.pages,
        copies: formData.copies,
        theme: formData.theme,
        questionFrequency: formData.questionFrequency,
        memoirTitle: formData.memoirTitle,
        memoirSubtitle: formData.memoirSubtitle,
        categoryId: formData.categoryId,
        deliveryDetails: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          country: formData.country
        },
        isGuestOrder
      };

      const response = await memoirService.createOrder(orderPayload);

      if (!response?.success || !response?.data?.payment?.authorizationUrl) {
        throw new Error(response?.message || 'Failed to initialize payment');
      }

      // Keep the order + reference locally so the verify page can recover
      // them if Paystack drops query params on return.
      setOrderData(response.data.order);
      try {
        localStorage.setItem(
          'kapta_pending_memoir_ref',
          response.data.payment.reference
        );
        localStorage.setItem(
          'kapta_pending_memoir_order',
          JSON.stringify(response.data.order)
        );
      } catch (_) {
        /* localStorage may be unavailable in some browsers — ignore */
      }

      // ✅ Redirect to Paystack's hosted checkout page.
      // Paystack will bring the user back to /memoirs/checkout/verify?reference=...
      window.location.href = response.data.payment.authorizationUrl;
    } catch (error) {
      console.error('Order placement error:', error);
      alert(error.message || 'Failed to place order. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmitStep5 = async () => {
    if (
      !formData.email ||
      !formData.phone ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.address ||
      !formData.city ||
      !formData.state
    ) {
      alert('Please fill in all required fields');
      return;
    }
    await handlePlaceOrder();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={nextStep}
          />
        );
      case 2:
        return (
          <Step2
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 3:
        return (
          <Step3
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={nextStep}
            prevStep={prevStep}
          />
        );
      case 4:
        return (
          <Step4
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={nextStep}
            prevStep={prevStep}
            categories={categories}
            loadingCategories={loadingCategories}
          />
        );
      case 5:
        return (
          <Step5
            formData={formData}
            handleInputChange={handleInputChange}
            nextStep={handleSubmitStep5}
            prevStep={prevStep}
            total={calculateTotal()}
            loading={loading}
          />
        );
      case 6:
        return (
          <Step6
            formData={formData}
            total={calculateTotal()}
            orderData={orderData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-[#FCF7EE] min-h-screen">
      <div className="container mx-auto px-4 sm:px-6">
        {currentStep === 6 ? (
          renderStep()
        ) : (
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="lg:w-1/2 pt-8 sm:pt-10 md:pt-12">
              {renderStep()}
            </div>
            <div className="lg:w-1/2">
              <SidebarInfo />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STEP 1 — Who is the memoir for?
   ═══════════════════════════════════════════════════════════════ */
const Step1 = ({ formData, handleInputChange, nextStep }) => {
  const options = [
    { id: 'myself', label: 'For Myself' },
    { id: 'someoneElse', label: 'For Someone Else' },
    { id: 'family', label: 'For Family/Legacy' }
  ];

  return (
    <div className="text-left px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-medium text-[#1C2A46] mb-4 sm:mb-6">
        Who are you creating Kapta Memoir for?
      </h2>
      <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
        {options.map((option) => (
          <label
            key={option.id}
            className={`block p-3 sm:p-4 border-2 cursor-pointer transition-all ${
              formData.memoirFor === option.id
                ? 'border-[#0F1F3D] bg-[#F7F5EF]'
                : 'border-gray-200 bg-[#F7F5EF] hover:border-[#D4A574]'
            }`}
          >
            <div className="flex items-center">
              <input
                type="radio"
                name="memoirFor"
                value={option.id}
                checked={formData.memoirFor === option.id}
                onChange={(e) => handleInputChange('memoirFor', e.target.value)}
                className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4A574] focus:ring-[#D4A574] mr-3"
              />
              <span className="font-normal text-[#1C2A46] text-sm sm:text-base">
                {option.label}
              </span>
            </div>
          </label>
        ))}
      </div>
      <div className="flex items-center text-xs sm:text-sm text-[#6B635D] mb-6 sm:mb-8">
        <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center mr-2">
          <CautionIcon size={16} />
        </div>
        <span className="font-light text-xs sm:text-sm">
          You can invite someone else to write or write on their behalf.
        </span>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button className="text-[#1C2A46] h-[50px] sm:h-[60px] w-full sm:w-[171px] text-[14px] sm:text-[16px] border border-[#DBDBDB] p-4 sm:p-5 hover:underline flex items-center justify-center">
          <ArrowLeft size={20} className="mr-2" /> Back
        </button>
        <button
          onClick={nextStep}
          disabled={!formData.memoirFor}
          className={`px-6 sm:px-8 py-2.5 sm:py-3 h-[50px] sm:h-[60px] w-full sm:w-[316px] font-medium transition-colors text-sm sm:text-base ${
            formData.memoirFor
              ? 'bg-[#D2A146] text-white hover:bg-[#c49364]'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STEP 2 — Book Options
   ═══════════════════════════════════════════════════════════════ */
const Step2 = ({ formData, handleInputChange, nextStep, prevStep }) => (
  <div className="text-left px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 pb-6 sm:pb-8 md:pb-10">
    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-medium text-[#1C2A46] mb-4 sm:mb-6">
      Book Options
    </h2>
    <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
      <div>
        <label className="block text-base sm:text-lg font-normal text-[#1C2A46] mb-2 sm:mb-3">
          How many pages should the book start with?
        </label>
        <select
          value={formData.pages}
          onChange={(e) => handleInputChange('pages', Number(e.target.value))}
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] text-sm sm:text-base"
        >
          <option value={100}>100 pages (included with purchase)</option>
          <option value={150}>150 pages</option>
          <option value={200}>200 pages</option>
        </select>
      </div>
      <div>
        <label className="block text-base sm:text-lg font-normal text-[#1C2A46] mb-2 sm:mb-3">
          How many copies would you like?
        </label>
        <select
          value={formData.copies}
          onChange={(e) => handleInputChange('copies', Number(e.target.value))}
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] text-sm sm:text-base"
        >
          <option value={1}>1 book</option>
          <option value={2}>2 books +₦250,000</option>
          <option value={3}>3 books +₦500,000</option>
          <option value={4}>4 books +₦750,000</option>
        </select>
        <div className="flex items-center text-xs sm:text-sm text-[#6B635D] mt-2 sm:mt-3">
          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center mr-2">
            <span className="text-[#6B635D] text-xs border border-[#6B635D] rounded-full w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center">
              i
            </span>
          </div>
          <span className="font-light text-xs sm:text-sm">
            Extra copies are cheaper when ordered now.
          </span>
        </div>
      </div>
      <div>
        <label className="block text-base sm:text-lg font-normal text-[#1C2A46] mb-2 sm:mb-3">
          Choose theme colour
        </label>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <button
            onClick={() => handleInputChange('theme', 'wine')}
            className={`relative p-1 border border-[#E4E4E4] w-full sm:w-[297px] ${
              formData.theme === 'wine'
                ? 'ring-2 ring-[#D4A574] ring-offset-2'
                : ''
            }`}
          >
            <div className="overflow-hidden bg-[#F7F5EF] border border-gray-200">
              <img
                src={LegacyColorBook}
                alt="Wine theme book"
                className="w-full h-auto object-cover"
              />
            </div>
            <p className="text-left mt-2 font-normal text-[#1C2A46] text-sm sm:text-base">
              Wine
            </p>
          </button>
          <button
            onClick={() => handleInputChange('theme', 'midnightBlue')}
            className={`relative p-1 border border-[#E4E4E4] w-full sm:w-[297px] ${
              formData.theme === 'midnightBlue'
                ? 'ring-2 ring-[#D4A574] ring-offset-2'
                : ''
            }`}
          >
            <div className="overflow-hidden bg-[#F7F5EF] border border-gray-200">
              <img
                src={LegacyColorBook2}
                alt="Midnight Blue theme book"
                className="w-full h-auto object-cover"
              />
            </div>
            <p className="text-left mt-2 font-normal text-[#1C2A46] text-sm sm:text-base">
              Midnight Blue
            </p>
          </button>
        </div>
      </div>
    </div>
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <button
        onClick={prevStep}
        className="text-[#1C2A46] h-[50px] sm:h-[60px] w-full sm:w-[171px] text-[14px] sm:text-[16px] border border-[#DBDBDB] p-4 sm:p-5 hover:underline flex items-center justify-center"
      >
        <ArrowLeft size={20} className="mr-2" /> Back
      </button>
      <button
        onClick={nextStep}
        className="px-6 sm:px-8 py-2.5 sm:py-3 h-[50px] sm:h-[60px] w-full sm:w-[316px] font-medium bg-[#D2A146] text-white hover:bg-[#c49364] transition-colors text-sm sm:text-base"
      >
        Continue
      </button>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   STEP 3 — About your Memoir
   ═══════════════════════════════════════════════════════════════ */
const Step3 = ({ formData, handleInputChange, nextStep, prevStep }) => (
  <div className="text-left px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16">
    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-medium text-[#1C2A46] mb-4 sm:mb-6">
      About your Memoir
    </h2>
    <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
      <div>
        <label className="block text-base sm:text-lg font-normal text-[#1C2A46] mb-2 sm:mb-3">
          Questions Frequency
        </label>
        <select
          value={formData.questionFrequency}
          onChange={(e) =>
            handleInputChange('questionFrequency', e.target.value)
          }
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] text-sm sm:text-base"
        >
          <option value="weekly">Weekly (recommended)</option>
          <option value="daily">Daily</option>
          <option value="biweekly">Bi-weekly</option>
        </select>
      </div>
      <div>
        <label className="block text-base sm:text-lg font-normal text-[#1C2A46] mb-2 sm:mb-3">
          Memoir Title
        </label>
        <input
          type="text"
          value={formData.memoirTitle}
          onChange={(e) => handleInputChange('memoirTitle', e.target.value)}
          placeholder="e.g., Becoming Whole — My Journey Through Healing"
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light text-sm sm:text-base"
        />
      </div>
      <div>
        <label className="block text-base sm:text-lg font-normal text-[#1C2A46] mb-2 sm:mb-3">
          Subtitle (optional)
        </label>
        <input
          type="text"
          value={formData.memoirSubtitle}
          onChange={(e) => handleInputChange('memoirSubtitle', e.target.value)}
          placeholder="e.g., A story about courage, faith, and resilience"
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light text-sm sm:text-base"
        />
      </div>
    </div>
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <button
        onClick={prevStep}
        className="text-[#1C2A46] h-[50px] sm:h-[60px] w-full sm:w-[171px] text-[14px] sm:text-[16px] border border-[#DBDBDB] p-4 sm:p-5 hover:underline flex items-center justify-center"
      >
        <ArrowLeft size={20} className="mr-2" /> Back
      </button>
      <button
        onClick={nextStep}
        className="px-6 sm:px-8 py-2.5 sm:py-3 h-[50px] sm:h-[60px] w-full sm:w-[316px] font-medium bg-[#D2A146] text-white hover:bg-[#c49364] transition-colors text-sm sm:text-base"
      >
        Continue
      </button>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   STEP 4 — Choose Book Category
   ═══════════════════════════════════════════════════════════════ */
const Step4 = ({
  formData,
  handleInputChange,
  nextStep,
  prevStep,
  categories,
  loadingCategories
}) => {
  const getBookTypeFromCategory = (categoryName) => {
    const typeMap = {
      'Achievement & Becoming': 'achievement',
      'Healing & Survival': 'healing',
      'Love & Relationships': 'love',
      'Family & Roots': 'family',
      'Parenting (Motherhood & Fatherhood)': 'parenting',
      'Business Trajectory': 'business',
      'Life Lessons & Reflections': 'life',
      'Tell your own story': 'own'
    };
    return typeMap[categoryName] || '';
  };

  const getImageForCategory = (categoryName) => {
    const imageMap = {
      'Achievement & Becoming': Achievment,
      'Healing & Survival': Healing,
      'Love & Relationships': LoveRelation,
      'Family & Roots': Family,
      'Parenting (Motherhood & Fatherhood)': Parenting,
      'Business Trajectory': Business,
      'Life Lessons & Reflections': LifeLessons,
      'Tell your own story': YourOwnStory
    };
    return imageMap[categoryName] || Achievment;
  };

  return (
    <div className="text-left px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 pb-4 sm:pb-5">
      <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-medium text-[#1C2A46] mb-4 sm:mb-6">
        What book would you like to create?
      </h2>
      {loadingCategories ? (
        <div className="flex justify-center items-center py-12 sm:py-16 md:py-20">
          <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-[#D2A146] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-6">
          {categories.map((category) => {
            const bookTypeId = getBookTypeFromCategory(category.name);
            return (
              <button
                key={category._id}
                onClick={() => handleInputChange('bookType', bookTypeId)}
                className={`relative overflow-hidden transition-all border-2 bg-white ${
                  formData.bookType === bookTypeId
                    ? 'border-[#0F1F3D]'
                    : 'border-gray-200 hover:border-[#D4A574]'
                }`}
              >
                <div className="relative">
                  <img
                    src={getImageForCategory(category.name)}
                    alt={category.name}
                    className="w-full h-[140px] sm:h-[160px] object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-12 sm:h-16"></div>
                  <h3 className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 text-white font-medium text-xs sm:text-sm text-left">
                    {category.name}
                  </h3>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button
          onClick={prevStep}
          className="text-[#1C2A46] h-[50px] sm:h-[60px] w-full sm:w-[171px] text-[14px] sm:text-[16px] border border-[#DBDBDB] p-4 sm:p-5 hover:underline flex items-center justify-center"
        >
          <ArrowLeft size={20} className="mr-2" /> Back
        </button>
        <button
          onClick={nextStep}
          disabled={!formData.bookType || loadingCategories}
          className={`px-6 sm:px-8 py-2.5 sm:py-3 h-[50px] sm:h-[60px] w-full sm:w-[316px] font-medium transition-colors text-sm sm:text-base ${
            formData.bookType && !loadingCategories
              ? 'bg-[#D2A146] text-white hover:bg-[#c49364]'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STEP 5 — Contact & Delivery
   ═══════════════════════════════════════════════════════════════ */
const Step5 = ({
  formData,
  handleInputChange,
  nextStep,
  prevStep,
  total,
  loading
}) => (
  <div className="text-left px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 pb-6 sm:pb-8 md:pb-10">
    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-medium text-[#1C2A46] mb-4 sm:mb-6">
      Let's get you set up!
    </h2>
    <div className="space-y-6 sm:space-y-8 mb-6 sm:mb-8">
      <div className="space-y-3 sm:space-y-4">
        <input
          type="email"
          placeholder="Enter your email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light text-sm sm:text-base"
          required
        />
        <div className="flex gap-2">
          <div className="w-20 sm:w-24 p-3 sm:p-4 bg-[#F7F5EF] border border-gray-200 text-[#6B635D] font-light flex items-center justify-center text-sm sm:text-base">
            +234
          </div>
          <input
            type="tel"
            placeholder="Enter your Phone number"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className="flex-1 p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light text-sm sm:text-base"
            required
          />
        </div>
        <div className="flex items-center text-xs sm:text-sm text-[#6B635D]">
          <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center mr-2">
            <span className="text-[#6B635D] text-xs border border-[#6B635D] rounded-full w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center">
              i
            </span>
          </div>
          <span className="font-light text-xs sm:text-sm">
            We'll use this to send order updates and access your memories.
          </span>
        </div>
      </div>
      <div>
        <h3 className="text-base sm:text-lg font-normal text-[#1C2A46] mb-3 sm:mb-4">
          Delivery Details
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
          <input
            type="text"
            placeholder="*Enter first name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            className="p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light text-sm sm:text-base"
            required
          />
          <input
            type="text"
            placeholder="*Enter last name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            className="p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light text-sm sm:text-base"
            required
          />
        </div>
        <input
          type="text"
          placeholder="Enter your address"
          value={formData.address}
          onChange={(e) => handleInputChange('address', e.target.value)}
          className="w-full p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] placeholder:text-[#6B635D] placeholder:font-light mb-3 sm:mb-4 text-sm sm:text-base"
          required
        />
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <select
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className="p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] text-sm sm:text-base"
            required
          >
            <option value="">City</option>
            <option value="lagos">Lagos</option>
            <option value="abuja">Abuja</option>
            <option value="port-harcourt">Port Harcourt</option>
          </select>
          <select
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className="p-3 sm:p-4 border border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] text-sm sm:text-base"
            required
          >
            <option value="">State</option>
            <option value="lagos">Lagos</option>
            <option value="abuja">FCT</option>
            <option value="rivers">Rivers</option>
          </select>
        </div>
        <select
          value={formData.country}
          onChange={(e) => handleInputChange('country', e.target.value)}
          className="p-3 sm:p-4 mt-4 sm:mt-5 border w-full border-gray-200 bg-[#F7F5EF] focus:outline-none focus:border-[#D4A574] text-[#1C2A46] text-sm sm:text-base"
        >
          <option value="">Country</option>
          <option value="nigeria">Nigeria</option>
        </select>
      </div>
    </div>
    <div className="flex flex-col sm:flex-row items-center gap-3">
      <button
        onClick={prevStep}
        disabled={loading}
        className="text-[#1C2A46] h-[50px] sm:h-[60px] w-full sm:w-[171px] text-[14px] sm:text-[16px] border border-[#DBDBDB] p-4 sm:p-5 hover:underline flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ArrowLeft size={20} className="mr-2" /> Back
      </button>
      <button
        onClick={nextStep}
        disabled={loading}
        className={`px-6 sm:px-8 py-2.5 sm:py-3 h-[50px] sm:h-[60px] w-full sm:w-[316px] font-medium transition-colors text-sm sm:text-base ${
          loading
            ? 'bg-gray-400 text-white cursor-not-allowed'
            : 'bg-[#D2A146] text-white hover:bg-[#c49364]'
        }`}
      >
        {loading ? 'Redirecting to payment…' : `Place Order - ₦${total.toLocaleString()}`}
      </button>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════
   STEP 6 — Success (used only if user comes back without redirect)
   ═══════════════════════════════════════════════════════════════ */
const Step6 = ({ formData, total, orderData }) => {
  const navigate = useNavigate();
  const getCategoryName = () => {
    const categoryMap = {
      'achievement': 'Achievement & Becoming',
      'healing': 'Healing & Survival',
      'love': 'Love & Relationships',
      'family': 'Family & Roots',
      'parenting': 'Parenting',
      'business': 'Business Trajectory',
      'life': 'Life Lessons & Reflections',
      'own': 'Your Own Story'
    };
    return categoryMap[formData.bookType] || 'Healing & Survival';
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="flex flex-col lg:flex-row gap-8 sm:gap-10 md:gap-12 max-w-6xl mx-auto">
        <div className="lg:w-1/2">
          <img
            src={CheckImage}
            alt="Photo album with memories"
            className="w-full max-w-[936px] h-auto"
          />
        </div>
        <div className="lg:w-1/2 text-left">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-medium text-[#1C2A46] mb-2">
            Your Memoir Journey
            <br />
            has Begun!
          </h2>
          <p className="text-[#6B635D] mb-6 sm:mb-8 text-xs sm:text-sm">
            We'll send your first guided question on WhatsApp within 24 hours.
          </p>
          <div className="bg-[#FCF7EE] mb-6 sm:mb-8 p-4 sm:p-0">
            <h3 className="text-lg sm:text-xl font-medium text-[#1C2A46] mb-3 sm:mb-4">
              Order Summary
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-4">
              <img
                src={CheckImage}
                alt="Photo album with memories"
                className="w-[100px] h-[130px] sm:w-[129px] sm:h-[162px]"
              />
              <div>
                <h4 className="font-medium text-[#1C2A46] text-base sm:text-lg">
                  Kapta Memoir
                </h4>
                <p className="text-[#55000C] font-light text-xs sm:text-sm">
                  {getCategoryName()}
                </p>
                {orderData?.orderNumber && (
                  <p className="text-[#6B635D] text-xs mt-1">
                    Order: {orderData.orderNumber}
                  </p>
                )}
                <p className="text-[#6B635D] mt-2 text-xs sm:text-sm">
                  Book Copies: {formData.copies}
                </p>
                <p className="text-[#6B635D] text-xs sm:text-sm">
                  Number of pages: {formData.pages}
                </p>
                <p className="text-sm sm:text-base mt-2 font-medium text-[#1C2A46]">
                  ₦{total.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/memoirs/my-memoir')}
            className="w-full py-3 sm:py-4 bg-[#D2A146] text-white font-medium hover:bg-[#c49364] transition-colors text-center block text-sm sm:text-base"
          >
            Go to My Memoir
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   SIDEBAR
   ═══════════════════════════════════════════════════════════════ */
const SidebarInfo = () => (
  <div className="border-l-2 pb-10 border-[#1C2A46] bg-[#F7F5EF] pt-8 sm:pt-10 md:pt-12 pl-6 sm:pl-8 md:pl-10 text-left mt-6 sm:mt-0">
    <div className="mb-4 sm:mb-6">
      <img
        src={photoAlbumImage}
        alt="Memoir books"
        className="w-[180px] h-[100px] sm:w-[200px] sm:h-[115px] md:w-[225px] md:h-[127px]"
      />
    </div>
    <h3 className="text-lg sm:text-xl font-bold text-[#1C2A46] mb-3 sm:mb-4">
      Preserve your parent's life story beautifully, in 90 days.
    </h3>
    <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
      A fully guided memoir experience. We interview, edit, design, and deliver
      a premium heirloom book.
    </p>
    <div className="mb-6 sm:mb-8">
      <h4 className="font-semibold text-base sm:text-lg text-[#1C2A46] mb-3 sm:mb-4">
        Included in the Purchase
      </h4>
      <ul className="space-y-2 sm:space-y-3">
        {[
          '12-week concierge memoir creation',
          'Dedicated Story Coordinator',
          'WhatsApp voice-note storytelling',
          'Professional editing & book design',
          'Premium hardcover printing',
          'Secure delivery in a keepsake box'
        ].map((item, index) => (
          <li key={index} className="flex items-start gap-2 sm:gap-3">
            <CheckIcon size={16} color="#1E1E1E" />
            <span className="text-[#1C2A46] text-sm sm:text-base">{item}</span>
          </li>
        ))}
      </ul>
    </div>
    <div className="border-t border-gray-200 pt-4 sm:pt-6">
      <p className="text-xl sm:text-2xl font-bold text-[#1C2A46] mb-2">
        Price Range: ₦250,000 - ₦500,000
      </p>
      <p className="text-[#6B635D] font-normal text-sm sm:text-base">
        Split payment Available
      </p>
    </div>
  </div>
);

export default CreateMemoir;